import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import axios from "axios";

import { RootState } from "src/redux/reducers";
import {
  ProjectSetupType,
  type SelectOption,
  type ProjectAction,
  type ProjectMetafield,
} from "../types";
import {
  isDataInBrief,
  isSubmissionFormEnabled,
  uploadCoverForNewProject,
  uploadCoverForResearchProject,
} from "../utils";
import { getRoute, ROUTES } from "src/utils/ROUTES";
import { AppLocation } from "src/redux/main/mainReducer";
import {
  clearProjectBasicData,
  clearTasksCounters,
  setTasksLoading,
} from "src/redux";
import {
  parseErrorMessages,
  showErrorToast,
  showSucessToast,
} from "src/utils/methods";
import { API_URLS } from "src/utils/API_URLS";
import { wsAxiosPost } from "src/helpers/wsAxios";
import { ApiResponse } from "src/types";
import responseCodes from "src/utils/responseCodes";
import { handle402Errors } from "src/redux/main/functions";
import { ProjectTemplate } from "src/redux/project/types";

interface UseSubmitProjectSetupProps {
  projectId: string;
  projectAction: ProjectAction;
  projectName: string;
  selectedCurrencyOption: string | null;
  selectedBaseTemplateOption: SelectOption | null;
  selectedExistingProjectOption: SelectOption | null;
  selectedSocialPlatformTemplateOptions: SelectOption[];
  currentProjectSetupOption: ProjectSetupType;
  coverImageFile: File | null;
  updatedProjectTemplates: ProjectTemplate[];
}

export function useSubmitProjectSetup({
  projectId,
  projectAction,
  projectName,
  selectedCurrencyOption,
  selectedBaseTemplateOption,
  selectedExistingProjectOption,
  selectedSocialPlatformTemplateOptions,
  currentProjectSetupOption,
  coverImageFile,
  updatedProjectTemplates,
}: UseSubmitProjectSetupProps) {
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    activeWorkspaceUuid,
    identity,
    createNewProjectData,
    newProjectBriefData,
  } = useSelector((state: RootState) => state.mainReducer);

  const { globalMetaFieldProjectWizardFields } = useSelector(
    (state: RootState) => state.projectReducer,
  );

  const handleCreateNewProject = async (
    projectTemplatesUuids: string[],
    projectMetaFieldsValues: ProjectMetafield[],
  ) => {
    setIsCreatingProject(true);
    const baseProjectSetupOptions = {
      wsProjectUuid: projectId,
      projectName: projectName.trim(),
      wsWorkspaceUuid: activeWorkspaceUuid,
      selectedCreatorsPayload:
        createNewProjectData?.selectedCreatorsPayload ?? [],
      selectedTasksType: createNewProjectData?.taskType,
    };

    const fields = [...projectMetaFieldsValues];
    const isBriefNotEmpty = isDataInBrief(newProjectBriefData);

    const briefData = {
      wsGlobalProjectMetaFieldUuid: identity.briefWsGlobalMetaFieldUuid ?? "",
      value: {
        campaignRequirements: newProjectBriefData?.result ?? "",
        key: newProjectBriefData?.key ?? "",
        fileName: newProjectBriefData?.name ?? "",
      },
    };

    if (isBriefNotEmpty) {
      fields.push(briefData);
    }

    try {
      if (
        currentProjectSetupOption === ProjectSetupType.BasedOnExistingProject
      ) {
        await axios.post(API_URLS.createNewProjectBasedOnExistingOne, {
          ...baseProjectSetupOptions,
          originalWsProjectUuid: selectedExistingProjectOption?.value,
          wsProjectMetaFieldsValues: isBriefNotEmpty ? [briefData] : [],
        });
      } else {
        await axios.post(API_URLS.createNewProject, {
          ...baseProjectSetupOptions,
          currencyCode: selectedCurrencyOption,
          wsProjectTemplatesUuids: projectTemplatesUuids,
          wsProjectMetaFieldsValues: fields,
        });
      }
      if (coverImageFile) {
        await uploadCoverForNewProject(
          coverImageFile,
          projectId,
          activeWorkspaceUuid,
        );
      }

      dispatch(setTasksLoading(true));
      dispatch(clearProjectBasicData());
      dispatch(clearTasksCounters());

      const singleProjectRoute = getRoute(ROUTES.PROJECT, {
        wsWorkspaceUuid: activeWorkspaceUuid,
        wsProjectUuid: projectId,
      });

      const isBasedOnExistingProject =
        currentProjectSetupOption === ProjectSetupType.BasedOnExistingProject;

      if (isBasedOnExistingProject) {
        history.push(
          getRoute(ROUTES.PROJECTS, {
            wsWorkspaceUuid: activeWorkspaceUuid,
          }),
        );
        showSucessToast({
          id: "ws_campaign_has_been_queued",
          defaultMessage:
            "Campaign has been queued and will appear in the campaigns list when ready.",
        });
      } else if (
        isSubmissionFormEnabled(
          selectedBaseTemplateOption,
          selectedSocialPlatformTemplateOptions,
          updatedProjectTemplates,
        )
      ) {
        const url = new URL(`${window.location.origin}${singleProjectRoute}`);
        url.searchParams.set(
          "view",
          JSON.stringify({
            howItWorks: { location: AppLocation.SingleProject },
          }),
        );
        history.push(
          `/workspace/${activeWorkspaceUuid}/projects/${projectId}${url.search}`,
        );
      } else {
        history.push(
          getRoute(ROUTES.PROJECT, {
            wsWorkspaceUuid: activeWorkspaceUuid,
            wsProjectUuid: projectId,
          }),
        );
      }

      if (!isBasedOnExistingProject) {
        showSucessToast({
          id: "ws_campaign_has_been_created",
          defaultMessage: "Campaign has been created",
        });
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.errors) {
        if (
          error.response.data.statusCode ===
          responseCodes["402_PAYMENT_REQUIRED"]
        ) {
          handle402Errors(dispatch, error?.response?.data?.errors);
        } else {
          const errorMessage = parseErrorMessages(error.response.data.errors)[0]
            .message;
          showErrorToast({
            id: errorMessage,
            defaultMessage: errorMessage,
          });
        }
      } else {
        showErrorToast({
          id: "ws_campaign_has_not_been_created",
          defaultMessage: "Campaign hasn't been created",
        });
      }
    } finally {
      setIsCreatingProject(false);
    }
  };

  const handleActivateProject = async (
    projectTemplatesUuids: string[],
    projectMetaFieldsValues: ProjectMetafield[],
  ) => {
    try {
      setIsCreatingProject(true);

      let coverData;
      if (coverImageFile) {
        coverData = await uploadCoverForResearchProject(coverImageFile);
      }

      const response = await wsAxiosPost<
        ApiResponse<{ wsProjectUuid: string }>
      >(API_URLS.activateProject, {
        wsProjectUuid: projectId,
        wsWorkspaceUuid: activeWorkspaceUuid,
        projectName: projectName.trim(),
        currencyCode: selectedCurrencyOption,
        wsProjectTemplatesUuids: projectTemplatesUuids,
        wsProjectMetaFieldsValues: projectMetaFieldsValues,
        selectedCreatorsPayload:
          createNewProjectData?.selectedCreatorsPayload ?? [],
        originalWsProjectUuid: selectedExistingProjectOption?.value,
        cover: coverData
          ? { key: coverData.key, fileName: coverData.fileName }
          : null,
      });

      if (response?.content?.wsProjectUuid) {
        const singleProjectRoute = getRoute(ROUTES.PROJECT, {
          wsWorkspaceUuid: activeWorkspaceUuid,
          wsProjectUuid: response.content.wsProjectUuid,
        });

        history.push({
          pathname: singleProjectRoute,
          search: "tab=creatorSearch",
        });
        history.go(0);
      }
    } catch (error) {
      console.error("handleActivateProject error: ", error);
    } finally {
      setIsCreatingProject(false);
    }
  };

  const submitProjectSetup = () => {
    const wsProjectTemplatesUuids: string[] = [];
    const wsProjectMetaFieldsValues: ProjectMetafield[] = [];

    if (currentProjectSetupOption === ProjectSetupType.Template) {
      selectedSocialPlatformTemplateOptions.forEach((option) =>
        wsProjectTemplatesUuids.push(option.value),
      );
      if (selectedBaseTemplateOption) {
        wsProjectTemplatesUuids.push(selectedBaseTemplateOption.value);
      }

      globalMetaFieldProjectWizardFields.forEach((field) => {
        wsProjectMetaFieldsValues.push({
          wsGlobalProjectMetaFieldUuid: field.uuid,
          value: field.value || field.defaultValue,
        });
      });
    }

    if (projectAction === "activate") {
      handleActivateProject(wsProjectTemplatesUuids, wsProjectMetaFieldsValues);
    } else {
      handleCreateNewProject(
        wsProjectTemplatesUuids,
        wsProjectMetaFieldsValues,
      );
    }
  };

  return { submitProjectSetup, isCreatingProject };
}
